// File: src/pages/Procurement/POMgmtDashboard.jsx
import React from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Boxes,
  ActivitySquare,
  FileBarChart2,
  ArrowLeft,
} from "lucide-react";
import { useUserPermissions } from "@/hooks/useUserPermissions";

/** Bright dashboard tile */
function GlowTile({ to, icon, title, description, gradient }) {
  return (
    <div className="group relative">
      <Link to={to} aria-label={title} className="block">
        <div
          className={`absolute -inset-[1px] rounded-2xl bg-gradient-to-r ${gradient} opacity-90 transition group-hover:opacity-100`}
        />
        <div className="relative rounded-2xl border border-slate-200 bg-white p-6 md:p-7 min-h-[180px] flex flex-col justify-between shadow-sm hover:shadow-lg hover:bg-slate-50 transition">
          <div className="flex items-center gap-4">
            <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 shadow-sm">
              {icon}
            </div>
            <h2 className="text-xl md:text-2xl font-bold tracking-tight text-slate-900">
              {title}
            </h2>
          </div>

          <p className="text-slate-600 text-sm md:text-[15px] mt-3 leading-relaxed">
            {description}
          </p>
        </div>
      </Link>
    </div>
  );
}

const POMgmtDashboard = () => {
  const {
    role,
    isAdmin,
    isFinance,
    isDeveloper,
    isProcurement,
    isManager,
    hasPermission,
  } = useUserPermissions();

  /*
   * Manager access is driven by server-resolved explicit permissions.
   * Legacy roles retain their existing UI behaviour during migration.
   *
   * DENY means the module is absent from this dashboard, not merely disabled.
   * Backend authorization remains authoritative for direct URL/API access.
   */
  const managerCanApprove =
    hasPermission("procurement.po.approve");

  const managerCanReceiving =
    hasPermission("procurement.receiving.view") ||
    hasPermission("procurement.receiving.receive") ||
    hasPermission("procurement.receiving.reject") ||
    hasPermission("procurement.receiving.backorder");

  const managerCanTimeline =
    hasPermission("procurement.po.view");

  const managerCanReport =
    hasPermission("report.procurement.view");

  const canApprove = isManager
    ? managerCanApprove
    : isAdmin || isFinance || isDeveloper;

  const canReceiving = isManager
    ? managerCanReceiving
    : isAdmin || isFinance || isDeveloper || isProcurement;

  const canTimeline = isManager
    ? managerCanTimeline
    : isAdmin || isDeveloper;

  const canReport = isManager
    ? managerCanReport
    : isAdmin || isFinance || isDeveloper;

  const noneAllowed = ![canApprove, canReceiving, canTimeline, canReport].some(
    Boolean
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50 text-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight bg-gradient-to-r from-sky-600 to-indigo-600 bg-clip-text text-transparent">
              PO Management Dashboard
            </h1>
            <p className="text-slate-600 mt-1">
              All PO workflows in one place. Built for speed, clarity, and
              control.
            </p>
          </div>

          <Link
            to="/procurement"
            className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 px-3 py-2 rounded-lg shadow-sm transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Procurement
          </Link>
        </div>

        {noneAllowed && (
          <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800 shadow-sm">
            Your role (<span className="font-semibold">{role || "unknown"}</span>)
            {" "}doesn’t have access to any PO management modules. Please contact an
            administrator if you believe this is an error.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-7">
          {canApprove ? (
            <GlowTile
              to="/procurement/po-approval"
              icon={<CheckCircle2 className="text-emerald-600" size={28} />}
              title="PO Approval Centre"
              description="Review, approve or reject purchase orders from unit managers."
              gradient="from-emerald-400 via-teal-400 to-cyan-400"
            />
          ) : null}

          {canReceiving ? (
            <GlowTile
              to="/po-receiving-dashboard"
              icon={<Boxes className="text-amber-600" size={28} />}
              title="Receiving Log"
              description="Log and monitor goods received against POs, including partial receipts and backorders."
              gradient="from-amber-400 via-orange-400 to-rose-400"
            />
          ) : null}

          {canTimeline ? (
            <GlowTile
              to="/procurement/po-timeline-log"
              icon={<ActivitySquare className="text-fuchsia-600" size={28} />}
              title="PO Timeline Logs"
              description="Track every PO activity event for audit and traceability."
              gradient="from-fuchsia-400 via-purple-400 to-indigo-400"
            />
          ) : null}

          {canReport ? (
            <GlowTile
              to="/procurement/report"
              icon={<FileBarChart2 className="text-sky-600" size={28} />}
              title="Procurement Report Panel"
              description="Analyze performance, filter by date/vendor/status, and export insights."
              gradient="from-sky-400 via-cyan-400 to-indigo-400"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default POMgmtDashboard;
